import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from '@zafroi/ui-kit';
import { Check, Mail } from 'lucide-react';
import type { BotPlanTier } from '@/types/engage';

/**
 * Upgrade plan dialog — informational, no real upgrade flow yet.
 *
 * Triggered by the "Upgrade plan" CTAs in `UsageStrip` and `UsageBanner`.
 * Uses the platform `Sheet` (side-mounted) as a lightweight modal — the
 * ui-kit does not expose a centered Dialog yet, and Sheet already ships
 * with the focus-trap, scroll-lock, and Escape-to-close we want.
 */

interface TierRow {
  tier: Exclude<BotPlanTier, 'BETA' | 'ENTERPRISE'>;
  name: string;
  price: string;
  cadence: string;
  blurb: string;
  includes: string[];
}

const TIERS: TierRow[] = [
  {
    tier: 'STARTER',
    name: 'Starter',
    price: '₹2,999',
    cadence: 'per month',
    blurb: 'For a first storefront getting customer chat live.',
    includes: [
      '1,000 bot turns / month',
      '2 agent seats',
      'WhatsApp, portal, and widget',
      'Email support',
    ],
  },
  {
    tier: 'GROWTH',
    name: 'Growth',
    price: '₹9,999',
    cadence: 'per month',
    blurb: 'For teams handling regular customer volume.',
    includes: [
      '5,000 bot turns / month',
      '5 agent seats',
      'All Starter features',
      'Priority support',
    ],
  },
  {
    tier: 'SCALE',
    name: 'Scale',
    price: '₹24,999',
    cadence: 'per month',
    blurb: 'For high-volume operations.',
    includes: [
      'Unlimited bot turns',
      'Unlimited agent seats',
      'All Growth features',
      'Dedicated success manager',
    ],
  },
];

export interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Highlight the recommended tier for the tenant's current state. */
  recommendedTier?: TierRow['tier'];
}

export function UpgradeModal({
  open,
  onOpenChange,
  recommendedTier,
}: UpgradeModalProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-lg sm:max-w-xl overflow-y-auto"
      >
        <SheetHeader className="pr-12">
          <SheetTitle>Upgrade your plan</SheetTitle>
          <SheetDescription>
            Pick the tier that matches your customer volume. All plans keep the
            same bot — only the included usage and seats change.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3 px-5 py-4">
          {TIERS.map((tier) => {
            const recommended = recommendedTier === tier.tier;
            return (
              <div
                key={tier.tier}
                className={cn(
                  'rounded-lg border p-4 transition-colors',
                  recommended
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-card',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">
                        {tier.name}
                      </span>
                      {recommended && (
                        <Badge tone="info" withDot={false}>
                          Recommended
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {tier.blurb}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold text-foreground">
                      {tier.price}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {tier.cadence}
                    </div>
                  </div>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {tier.includes.map((line) => (
                    <li
                      key={line}
                      className="flex items-start gap-2 text-xs text-foreground"
                    >
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          <p className="pt-2 text-xs leading-relaxed text-muted-foreground">
            Need something custom or have more than 50 agents? We also offer an
            Enterprise plan tailored to your volume.
          </p>
        </div>

        <div className="flex flex-col gap-2 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Maybe later
          </Button>
          <Button size="sm" asChild>
            <a href="mailto:hello@zafroi.com?subject=Engage%20plan%20upgrade">
              <Mail className="h-3.5 w-3.5" />
              Talk to us
            </a>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
